import { memo, useEffect, useState } from "react";
import { Box, Divider, Fab, TextField, FormLabel, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconCheck } from "@tabler/icons-react";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { updateNote } from "../../../gql/mutations/notes";
import { getAllNotes } from "../../../gql/queries/notes";
import useQuery from "../../../hooks/allQuery";

function NoteContent({ colorvariation, setNoteDetails, noteDetails, notesFilter, allNotes }) {
  const { userData } = useSelector(state => state.reducer);
  const [UPDATE_NOTE] = useMutation(updateNote, { fetchPolicy: "network-only" });
  const { allQuery } = useQuery();
  const [changeValue, setValueChange] = useState("");
  const [editNoteError, setEditNoteError] = useState(false);

  const handleNoteUpdate = (field, value) => {
    setNoteDetails(prevNoteDetails => ({
      ...prevNoteDetails,
      [field]: value,
    }));
    setValueChange(value);
  };

  const isValidNote = text => {
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed.split(/\s+/).length > 25;
  };

  useEffect(() => {
    const debouncedUpdateNote = debounce(() => {
      const trimmedNote = noteDetails?.note.trim().replace(/^\s+/, " ");
      if (changeValue && noteDetails && isValidNote(noteDetails.note) && noteDetails.note && noteDetails.color && noteDetails.id) {
        allQuery({
          QUERY: UPDATE_NOTE,
          variables: {
            data: {
              note: trimmedNote,
              color: noteDetails?.color,
            },
            updateNoteId: noteDetails.id,
          },
          onCompleteFunc: () => {
            toast.success("Notes updated successfully");
          },
          type: "update",
          cacheQueryData: {
            cacheQuery: getAllNotes,
            variablesCache: {
              userId: userData.id,
              searchOptions: notesFilter.searchOptions,
            },
          },
        });
      }
    }, 2000);

    debouncedUpdateNote();

    return () => {
      debouncedUpdateNote.cancel();
    };
  }, [changeValue]);

  return (
    <Box sx={{ height: { lg: "calc(100vh - 250px)", sm: "100vh" }, maxHeight: "700px" }}>
      <Divider />
      {allNotes.length > 0 ? (
        noteDetails ? (
          <Box p={3}>
            <FormLabel htmlFor="outlined-multiline-static">
              <Typography variant="h6" mb={2} fontWeight={600} color="text.primary">
                Edit Note
                <Tooltip
                  title="Your changes are saved automatically."
                  placement="top"
                  arrow
                  PopperProps={{
                    sx: {
                      "& .MuiTooltip-tooltip": {
                        mb: "10px !important",
                        backgroundColor: "#539BFF",
                        color: "#ffffff",
                        fontSize: "12px",
                      },
                      "& .MuiTooltip-arrow": {
                        color: "#539BFF",
                      },
                    },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: "18px", marginLeft: "4px", marginTop: "-2px", cursor: "pointer" }} />
                </Tooltip>
              </Typography>
            </FormLabel>

            <TextField
              id="outlined-multiline-static"
              placeholder="Edit Note"
              multiline
              fullWidth
              rows={5}
              variant="outlined"
              value={noteDetails.note}
              onChange={e => {
                const value = e.target.value;
                const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
                handleNoteUpdate("note", capitalizedValue);
                const isValid = isValidNote(capitalizedValue);
                setEditNoteError(!isValid);
              }}
              error={editNoteError}
              helperText={editNoteError && "Description must contain atleast 25 words"}
              sx={{ "& .MuiInputBase-root": { padding: "0" } }}
            />
            <br />
            <Typography variant="h6" mt={3} mb={2} fontWeight={600}>
              Change Note Color
            </Typography>

            {colorvariation.map(color1 => (
              <Tooltip
                title={color1.disp}
                placement="top"
                arrow
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      mb: "10px !important",
                      backgroundColor: color1.bgColor,
                      color: "#ffffff",
                      textTransform: "capitalize",
                    },
                    "& .MuiTooltip-arrow": {
                      color: color1.bgColor,
                    },
                  },
                }}
              >
                <Fab
                  sx={{
                    marginRight: "3px",
                    boxShadow: "none",
                    transition: "0.1s ease-in",
                    scale: noteDetails.color === color1.disp ? "0.9" : "0.7",
                    backgroundColor: color1.bgColor,
                    "&:hover": {
                      backgroundColor: color1.bgColor + "d4",
                    },
                  }}
                  size="small"
                  key={color1.id}
                  onClick={() => handleNoteUpdate("color", color1.disp)}
                >
                  {noteDetails.color === color1.disp ? <IconCheck width="16" color="white" /> : ""}
                </Fab>
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Box sx={{ fontSize: "24px", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Select a Note</Box>
        )
      ) : (
        <Box sx={{ fontSize: "24px", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>No notes available at the moment.</Box>
      )}
    </Box>
  );
}

export default memo(NoteContent);
